import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'

import { Collapsible } from 'app/KinerjaShared/components/Collapsible'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaShared/components/Card'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import { tugasPokok } from 'app/KinerjaPage/constants'
import {
  API_PATH_TUGAS_POKOK,
  useAPIGetRealisasiSKP,
  useAPIGetTugasPokok,
  useAPIPelaksanaanProgress,
  useAPISubmitTugasPokok,
} from 'api/kinerja/useAPIKinerja'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  isSelectableTugasPokok,
  resolvePerencanaanDetailData,
} from 'app/KinerjaGuruPage/utils/perencanaan'
import {
  useAPIKinerja,
  useAPIContentKinerjaUtama,
} from 'api/kinerja/useAPIKinerja'

import type { MultiPeriodParams, KinerjaUtamaDetail } from 'types/kinerja'
import type { PerencanaanGuruDetailData } from 'app/KinerjaGuruPage/types/perencanaan'

type PelaksanaanTugasPokokProps = {
  year?: number
  isReadOnly?: boolean
}

export function PelaksanaanTugasPokok({
  year,
  isReadOnly = false,
}: PelaksanaanTugasPokokProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { tipe } = useKinerjaTipe()
  const { periodYear, periodNo } = usePeriodContext()

  // Override period params only if the year param exist
  const resolvedYear = year ?? periodYear
  const periodParams = { skp_period: periodNo, skp_year: resolvedYear }

  // ----------------------------
  // Handle checkbox
  // ----------------------------
  const [isChecked, setIsChecked] = useState(false)

  const { data: dataTugasPokokState, isLoading: isLoadingTugasPokokState } =
    useAPIGetTugasPokok({
      params: { periodParams, tipe },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: dataPelaksanaan } = useAPIPelaksanaanProgress({
    periodParams,
  })

  // ----------------------------
  // Handle submission
  // ----------------------------
  const tugasPokokMutation = useAPISubmitTugasPokok()

  const handleSubmit = () => {
    if (isFormDisabled) return

    tugasPokokMutation.mutate(
      {
        tipe,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_TUGAS_POKOK)
          toast({
            message: 'Berhasil simpan pernyataan',
            type: 'success',
          })
        },
        onError: () => {
          toast({
            message: 'Gagal simpan pernyataan. Coba lagi.',
          })
        },
      }
    )
  }

  // ----------------------------
  // Derived values
  // ----------------------------
  const state = dataTugasPokokState?.data?.state

  const isSubmitted = state === 'SUBMITTED' || state === 'APPROVED'
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isFormDisabled = isReadOnly || isSubmitted || isEvaluated

  const isLoading = isLoadingTugasPokokState || tugasPokokMutation.isLoading

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto">
        <Header
          title="Pelaksanaan Tugas Pokok"
          className="mb-9 print:hidden"
          onBack={() => {
            router.back()
          }}
        />

        <div className="flex items-start gap-8">
          <aside className="w-96 flex-shrink-0 flex-grow-0 p-4 flex gap-2 rounded-lg border border-gray-50">
            <Icon fontSize="sm" className="!text-xl !leading-5">
              info
            </Icon>

            <ul className="list-disc pl-6 space-y-2">
              <Text as="li">
                <strong>
                  Guru tetap menyelesaikan Bukti Dukung Tugas Pokok di luar
                  sistem
                </strong>{' '}
                sesuai peraturan perundang-undangan.
              </Text>
              <Text as="li">
                <strong>
                  Tidak perlu unggah Bukti Dukung di dalam sistem.
                </strong>{' '}
                Cukup tunjukkan Bukti Dukung ke Kepala Sekolah.
              </Text>
            </ul>
          </aside>
          <Card withBorder>
            <CardHeader>
              <Text variant="heading-lg" as="h2">
                Tekan pernyataan di bawah jika Anda sudah melakukan Pelaksanaan
                Tugas Pokok
              </Text>
            </CardHeader>

            <CardBody className="p-6 space-y-4">
              <Table withOuterBorder rounded>
                <TableHeader>
                  <TableHeaderItem>No.</TableHeaderItem>
                  <TableHeaderItem>Rencana Hasil Kerja</TableHeaderItem>
                </TableHeader>
                <TableBody>
                  {periodParams.skp_year >= 2026 ? (
                    <RowsV2 periodParams={periodParams} />
                  ) : (
                    <Rows periodParams={periodParams} />
                  )}
                </TableBody>
              </Table>

              <div className="bg-surface-selected-default p-4 rounded-lg flex">
                <Checkbox
                  labelPosition="center"
                  className="flex"
                  disabled={isLoading || isFormDisabled}
                  checked={isChecked || isSubmitted}
                  onChange={(event) => {
                    setIsChecked(event.currentTarget.checked)
                  }}
                >
                  <Text as="span" variant="body-sm-bold">
                    Saya menyatakan sudah melakukan Pelaksanaan Tugas Pokok
                  </Text>
                </Checkbox>
              </div>
            </CardBody>

            {!isFormDisabled && (
              <CardFooter withSeparator={false} className="pt-0">
                <Button
                  size="lg"
                  className="ml-auto"
                  isLoading={isLoading}
                  disabled={isLoading || isFormDisabled || !isChecked}
                  onClick={handleSubmit}
                >
                  {isLoading ? 'Memuat..' : 'Simpan'}
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

// Prior to 2026
function Rows({ periodParams }: { periodParams: MultiPeriodParams }) {
  // ----------------------------a
  // Get Tugas Tambahan data
  // ----------------------------
  const { data: dataTugasTambahan, isLoading } = useAPIGetRealisasiSKP({
    params: {
      kategori: ['TT'],
      periodParams,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  if (isLoading) {
    return <TableShimmer columns={2} rows={5} />
  }

  return (
    <>
      {tugasPokok.map((item, index) => (
        <TableRow key={index}>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {index + 1}
          </Text>
          <Text as={TableRowItem}>{item}</Text>
        </TableRow>
      ))}

      {dataTugasTambahan?.data?.map((item, index) => (
        <TableRow key={index}>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {index + tugasPokok.length + 1}
          </Text>
          <Text as={TableRowItem}>{item.deskripsi}</Text>
        </TableRow>
      ))}
    </>
  )
}

// 2026 onwards
function RowsV2({ periodParams }: { periodParams?: MultiPeriodParams }) {
  const { tipe } = useKinerjaTipe()

  // -----------------------------------------
  // Get content data for mapping
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe,
      periodParams,
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  // Content as map for constant time lookup
  const tugasPokokcontentsMap: Record<number, KinerjaUtamaDetail> =
    useMemo(() => {
      const contents = dataContentKinerjaUtama?.data?.kinerjaUtama || []

      return contents.reduce((acc, content) => {
        acc[content.id] = content
        return acc
      }, {} as Record<number, KinerjaUtamaDetail>)
    }, [dataContentKinerjaUtama?.data?.kinerjaUtama])

  const tugasTambahancontentsMap: Record<number, KinerjaUtamaDetail> =
    useMemo(() => {
      const contents = dataContentKinerjaUtama?.data?.tugasTambahan || []

      return contents.reduce((acc, content) => {
        acc[content.id] = content
        return acc
      }, {} as Record<number, KinerjaUtamaDetail>)
    }, [dataContentKinerjaUtama?.data?.tugasTambahan])

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe,
        ...periodParams,
      }
    )

  const resolvedPerencanaanData: PerencanaanGuruDetailData = useMemo(
    () => resolvePerencanaanDetailData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  // -----------------------------------------
  // Resolve the mandatory and selectable tugas pokok
  // -----------------------------------------
  const {
    mandatoryTugasPokok,
    selectableTugasPokok,
  }: {
    mandatoryTugasPokok: Array<number>
    selectableTugasPokok: Array<number>
  } = useMemo(() => {
    // Use `reviewTugasPokokIds` instead of `tugasPokokIds` as it represents the final tugas pokok ids
    const tugasPokokIds = resolvedPerencanaanData.reviewTugasPokokIds

    const mandatory = []
    const selectable = []

    tugasPokokIds.forEach((id) => {
      const content = tugasPokokcontentsMap[id]

      if (content) {
        if (isSelectableTugasPokok(content.tag)) {
          selectable.push(content.id)
        } else {
          mandatory.push(content.id)
        }
      }
    })
    return {
      mandatoryTugasPokok: mandatory,
      selectableTugasPokok: selectable,
    }
  }, [resolvedPerencanaanData.reviewTugasPokokIds, tugasPokokcontentsMap])

  // -----------------------------------------
  // Derived values
  // -----------------------------------------

  const isLoading = isPerencanaanLoading || isLoadingContentKinerjaUtama

  if (isLoading) {
    return <TableShimmer columns={2} rows={5} />
  }

  return (
    <>
      {/* Mandatory Tugas Pokok */}
      {mandatoryTugasPokok.map((tugasPokokId, index) => (
        <TableRow key={index}>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {index + 1}
          </Text>
          <Text as={TableRowItem}>
            {tugasPokokcontentsMap[tugasPokokId]?.rencanaHasilKerja}
          </Text>
        </TableRow>
      ))}

      {/* Selectable Tugas Pokok */}

      {selectableTugasPokok.length > 0 && (
        <TableRow>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {mandatoryTugasPokok.length + 1}
          </Text>
          <Text as={TableRowItem}>
            <Collapsible title="Terlaksananya pembimbingan dan pelatihan dalam mewujudkan pembelajaran yang bermutu untuk semua">
              <ul className="list-disc pl-6 mt-2 space-y-3">
                {selectableTugasPokok.map((item, index) => (
                  <Text as="li" key={index}>
                    {tugasPokokcontentsMap[item]?.targetKualitas}
                  </Text>
                ))}
              </ul>
            </Collapsible>
          </Text>
        </TableRow>
      )}

      {/* Tugas Tambahan */}
      {resolvedPerencanaanData.reviewTugasTambahanIds.length > 0 && (
        <TableRow>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {mandatoryTugasPokok.length + 2}
          </Text>
          <Text as={TableRowItem}>
            <Collapsible title="Terlaksananya tugas tambahan dalam mendukung pembelajaran yang bermutu untuk semua">
              <ul className="list-disc pl-6 mt-2 space-y-3">
                {resolvedPerencanaanData.reviewTugasTambahanIds.map(
                  (item, index) => (
                    <Text as="li" key={index}>
                      {tugasTambahancontentsMap[item]?.rencanaHasilKerja}
                    </Text>
                  )
                )}
              </ul>
            </Collapsible>
          </Text>
        </TableRow>
      )}
    </>
  )
}
